import { Box } from "@mui/material"
import React, { useState } from "react"
import type { FieldValues, Path } from "react-hook-form"

import UploadFileLabel from "@/components/upload/components/UploadFileLabel"
import FileInput from "@/components/upload/FileInput"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"

interface FileAreaProps<T extends FieldValues> {
  name: Path<T>
  title?: string
  referenceId?: string
  referenceTableName: ReferenceTableName
  fieldGroups?: FieldGroupEnum[]
  onMarkFileAsNeedRemoval?: (fileId: string) => void
  showUploadAreaByDefault?: boolean
  pause?: boolean
  allowedMimeTypes?: string[]
  allowedTypesLabel?: string
  uploadAreaTitle?: string
}

const FileArea = <T extends FieldValues>({
  name,
  title = "",
  referenceId,
  fieldGroups,
  referenceTableName,
  onMarkFileAsNeedRemoval,
  showUploadAreaByDefault = true,
  pause = false,
  allowedMimeTypes,
  allowedTypesLabel,
  uploadAreaTitle,
}: FileAreaProps<T>): React.ReactElement | null => {
  const [showUploadArea, setShowUploadArea] = useState(!!showUploadAreaByDefault)

  const { fileList, fileFetching } = useGetFileUploadData({
    referenceId,
    fieldGroups,
    referenceTableName,
    pause: !referenceId || pause,
  })

  const file = fileList?.[0]?.file

  if (file || fileFetching || showUploadArea) {
    return (
      <FileInput<T>
        name={name}
        className='mt-4'
        title={title}
        fileData={file as File}
        onChange={
          onMarkFileAsNeedRemoval && file?.fileId
            ? () => {
                onMarkFileAsNeedRemoval(file.fileId!)
              }
            : undefined
        }
        allowedMimeTypes={allowedMimeTypes}
        allowedTypesLabel={allowedTypesLabel}
        fileFetching={fileFetching}
        uploadAreaTitle={uploadAreaTitle}
      />
    )
  } else {
    return (
      <Box className='my-4 flex justify-end'>
        <UploadFileLabel onClick={() => setShowUploadArea(true)} />
      </Box>
    )
  }
}

export default FileArea
